import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, Wallet, CheckCircle, AlertTriangle, Coins, Gift } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequest: {
    id: string;
    title: string;
    vendor?: {
      name: string;
      rating: number;
    };
    totalAmount: number;
    groupTokensAvailable?: number;
    isGroupBuy?: boolean;
  };
  userWallet?: {
    balance: number;
    groupTokens: number;
    creditCards: Array<{
      id: string;
      last4: string;
      brand: string;
      isDefault: boolean;
    }>;
  };
}

export function PaymentModal({ isOpen, onClose, serviceRequest, userWallet }: PaymentModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card' | 'tokens'>('wallet');
  const [useGroupTokens, setUseGroupTokens] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string>('');

  // Mock wallet data if not provided
  const wallet = userWallet || {
    balance: 250.0,
    groupTokens: 450,
    creditCards: [
      { id: 'card1', last4: '4242', brand: 'Visa', isDefault: true },
      { id: 'card2', last4: '8888', brand: 'MasterCard', isDefault: false },
    ],
  };

  const tokenValue = 0.5; // $0.50 per token
  const maxTokensToUse = Math.min(
    serviceRequest.groupTokensAvailable || 0,
    wallet.groupTokens,
    Math.floor(serviceRequest.totalAmount / tokenValue),
  );
  const tokenDiscount = useGroupTokens ? maxTokensToUse * tokenValue : 0;
  const finalAmount = serviceRequest.totalAmount - tokenDiscount;

  const canPay = () => {
    if (paymentMethod === 'wallet') {
      return wallet.balance >= finalAmount;
    } else if (paymentMethod === 'card') {
      return selectedCard !== '';
    } else if (paymentMethod === 'tokens') {
      return wallet.groupTokens >= Math.ceil(serviceRequest.totalAmount / tokenValue);
    }
    return false;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Preview
          </DialogTitle>
          <DialogDescription>
            Cost breakdown for "{serviceRequest.title}". Payments are not yet enabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Preview-only notice */}
          <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900">
              This is a preview of what you would be charged. Online payment is not available yet,
              so nothing is charged and no payment is recorded. Arrange payment with the vendor
              directly.
            </AlertDescription>
          </Alert>

          {/* Service Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Service Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span>Service Fee:</span>
                <span className="font-medium">${serviceRequest.totalAmount.toFixed(2)}</span>
              </div>

              {serviceRequest.isGroupBuy && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                    Group Buy Discount Applied
                  </Badge>
                </div>
              )}

              {serviceRequest.vendor && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Vendor: {serviceRequest.vendor.name}</span>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>{serviceRequest.vendor.rating}★</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Group Tokens */}
          {serviceRequest.groupTokensAvailable > 0 && (
            <Card className="border-purple-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Coins className="w-4 h-4 text-purple-600" />
                  Group Tokens Available
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Use {maxTokensToUse} tokens for discount</p>
                    <p className="text-xs text-muted-foreground">
                      You have {wallet.groupTokens} tokens • ${tokenValue} each
                    </p>
                  </div>
                  <Switch
                    checked={useGroupTokens}
                    onCheckedChange={setUseGroupTokens}
                    disabled={maxTokensToUse === 0}
                  />
                </div>

                {useGroupTokens && (
                  <div className="flex justify-between text-sm bg-purple-50 rounded p-2">
                    <span className="text-purple-700">Token Discount:</span>
                    <span className="font-medium text-purple-700">
                      -${tokenDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Methods */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payment Method</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Wallet Balance */}
              <div
                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                  paymentMethod === 'wallet' ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
                }`}
                onClick={() => setPaymentMethod('wallet')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Community Wallet</p>
                      <p className="text-sm text-muted-foreground">
                        Balance: ${wallet.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <input
                    type="radio"
                    checked={paymentMethod === 'wallet'}
                    onChange={() => setPaymentMethod('wallet')}
                    className="w-4 h-4"
                  />
                </div>
              </div>

              {/* Credit Cards */}
              <div
                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                  paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
                }`}
                onClick={() => setPaymentMethod('card')}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5" />
                    <p className="font-medium">Credit/Debit Card</p>
                  </div>
                  <input
                    type="radio"
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                    className="w-4 h-4"
                  />
                </div>

                {paymentMethod === 'card' && (
                  <Select value={selectedCard} onValueChange={setSelectedCard}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a card" />
                    </SelectTrigger>
                    <SelectContent>
                      {wallet.creditCards.map((card) => (
                        <SelectItem key={card.id} value={card.id}>
                          {card.brand} •••• {card.last4} {card.isDefault && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Group Tokens Only */}
              {wallet.groupTokens >= Math.ceil(serviceRequest.totalAmount / tokenValue) && (
                <div
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    paymentMethod === 'tokens'
                      ? 'border-purple-500 bg-purple-50'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => setPaymentMethod('tokens')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Gift className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium">Pay with Tokens Only</p>
                        <p className="text-sm text-muted-foreground">
                          Use {Math.ceil(serviceRequest.totalAmount / tokenValue)} tokens
                        </p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      checked={paymentMethod === 'tokens'}
                      onChange={() => setPaymentMethod('tokens')}
                      className="w-4 h-4"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Total */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total Amount:</span>
                  <span className="text-green-600">
                    {paymentMethod === 'tokens'
                      ? `${Math.ceil(serviceRequest.totalAmount / tokenValue)} tokens`
                      : `$${finalAmount.toFixed(2)}`}
                  </span>
                </div>

                {tokenDiscount > 0 && (
                  <div className="text-sm text-muted-foreground">
                    Original: ${serviceRequest.totalAmount.toFixed(2)} - Token Discount: $
                    {tokenDiscount.toFixed(2)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Warnings */}
          {!canPay() && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {paymentMethod === 'wallet' &&
                  wallet.balance < finalAmount &&
                  `Insufficient wallet balance. You need $${(finalAmount - wallet.balance).toFixed(2)} more.`}
                {paymentMethod === 'card' &&
                  !selectedCard &&
                  'Please select a credit card to continue.'}
                {paymentMethod === 'tokens' &&
                  wallet.groupTokens < Math.ceil(serviceRequest.totalAmount / tokenValue) &&
                  'Insufficient tokens for full payment.'}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Close
          </Button>
          <Button disabled className="flex-1" title="Online payment is not available yet">
            Payment Unavailable
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
